import run from "@/apps/shared/Onoff";
import { gatewayUrl, hasNetworkDivergence, syncGatewayTokenFromConfig } from "./gatewayClient";

const RETRY_DELAY_MS = 10000;
const HEARTBEAT_DELAY_MS = 20000;
const MAX_UNREACHABLE_RETRIES = 3;
let socket = null;
let retryTimer = null;
let heartbeatTimer = null;
let stopped = true;
let status = "disconnected";
let logs = [];
let activeConfigSignature = "";
let unreachableRetries = 0;
const pendingRequests = new Map();

function publishLog(message, level = "info") {
  logs = [{ message, level, createdAt: new Date() }, ...logs].slice(0, 40);
  window.dispatchEvent(new CustomEvent("stonepro:whatsapp-gateway-log", { detail: logs }));
}

function publishStatus(nextStatus, detail = {}) {
  if (status === nextStatus && !Object.keys(detail).length) return;
  status = nextStatus;
  window.dispatchEvent(new CustomEvent("stonepro:whatsapp-gateway-status", {
    detail: { status, ...detail },
  }));
}

function clearTimers() {
  window.clearTimeout(retryTimer);
  window.clearInterval(heartbeatTimer);
  retryTimer = null;
  heartbeatTimer = null;
}

function scheduleReconnect() {
  if (stopped || retryTimer) return;
  retryTimer = window.setTimeout(() => {
    retryTimer = null;
    void connect();
  }, RETRY_DELAY_MS);
}

function scheduleGatewayUnavailableReconnect() {
  unreachableRetries += 1;
  if (unreachableRetries >= MAX_UNREACHABLE_RETRIES) {
    stopped = true;
    publishLog("Gateway local inacessivel. Verifique se o Loop esta aberto e ouvindo na porta configurada, depois clique em Reconectar.", "error");
    return;
  }
  scheduleReconnect();
}

function configSignature(config = {}) {
  return [
    config.gateway_host,
    config.gateway_port,
    config.gateway_token,
    config.session_id,
  ].map(value => String(value || "").trim()).join("|");
}

function send(name, payload = {}) {
  if (socket?.readyState !== WebSocket.OPEN) return;
  socket.send(JSON.stringify({ id: `${name}-${Date.now()}`, name, payload }));
}

function readLoggedUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}") || {};
  } catch {
    return {};
  }
}

function readStoredToken() {
  try {
    return JSON.parse(localStorage.getItem("token") || "\"\"") || "";
  } catch {
    return localStorage.getItem("token") || "";
  }
}

function decodeTokenPayload() {
  const token = String(readStoredToken() || "");
  const [, rawPayload] = token.split(".");
  if (!rawPayload) return {};
  try {
    const base64 = rawPayload.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "="))) || {};
  } catch {
    return {};
  }
}

function userIdFrom(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  return String(value._id || value.id || value.user_id || value.userId || "").trim();
}

function currentUserIdentity() {
  const user = readLoggedUser();
  const tokenPayload = decodeTokenPayload();
  const ids = [
    userIdFrom(user),
    userIdFrom(tokenPayload),
    tokenPayload.sub,
    tokenPayload.userId,
    tokenPayload.user_id,
  ].map(value => String(value || "").trim()).filter(Boolean);
  return {
    user,
    ids: [...new Set(ids)],
    name: String(user?.nome || user?.name || tokenPayload?.nome || tokenPayload?.name || user?.username || "Atendente"),
    email: String(user?.email || tokenPayload?.email || ""),
  };
}

function currentTenantId() {
  const user = readLoggedUser();
  return String(user?.activeTenant?._id || user?.activeTenant || user?.tenant?.[0]?._id || user?.tenant?.[0] || "").trim();
}

function assignedUserIdFrom(conversation) {
  return userIdFrom(conversation?.assigned_user || conversation?.assignedUser || conversation?.assigned_user_id);
}

function conversationContactName(conversation) {
  return String(
    conversation?.client?.nome ||
    conversation?.contact?.name ||
    conversation?.contact_name ||
    conversation?.name ||
    conversation?.chat_name ||
    conversation?.chat_id ||
    "Cliente"
  );
}

function conversationContactPhone(conversation) {
  return String(
    conversation?.client?.whatsapp_phone ||
    conversation?.contact?.phone ||
    conversation?.contact_phone ||
    conversation?.phone ||
    conversation?.chat_id ||
    ""
  );
}

function compactConversation(conversation) {
  return {
    id: String(conversation?._id || conversation?.id || conversation?.chat_id || ""),
    contactName: conversationContactName(conversation),
    contactPhone: conversationContactPhone(conversation),
    unreadCount: Number(conversation?.unread_count || conversation?.unreadCount || 0),
    lastMessageText: String(conversation?.last_message_text || conversation?.lastMessageText || conversation?.last_message || ""),
    updatedAt: conversation?.updated_at || conversation?.updatedAt || conversation?.last_message_at || conversation?.created_at || "",
  };
}

function firstText(values = []) {
  return values.map(value => String(value || "").trim()).find(Boolean) || "";
}

function baileysKey(message = {}, payload = {}) {
  return message.key || payload.key || message.raw?.key || payload.raw?.key || message.rawMessage?.key || payload.rawMessage?.key || {};
}

function normalizeIncomingMessage(payload = {}) {
  const message = payload.message || {};
  const key = baileysKey(message, payload);
  const contact = message.contact || payload.contact || {};
  const sender = message.sender || payload.sender || {};
  const raw = message.raw || payload.raw || {};
  const rawMessage = message.rawMessage || payload.rawMessage || {};
  const chatId = firstText([
    message.chatId,
    message.chat_id,
    payload.chatId,
    payload.chat_id,
    message.groupId,
    payload.groupId,
    key.remoteJid,
    message.remoteJid,
    message.remote_jid,
    payload.remoteJid,
    payload.remote_jid,
    message.jid,
    payload.jid,
  ]);
  const senderId = firstText([
    message.senderId,
    message.sender_id,
    payload.senderId,
    payload.sender_id,
    message.sender,
    payload.sender,
    sender.id,
    sender.jid,
    message.participant,
    payload.participant,
    message.author,
    payload.author,
    key.participant,
  ]);
  const senderPhone = firstText([
    message.senderPhone,
    message.sender_phone,
    payload.senderPhone,
    payload.sender_phone,
    message.senderPn,
    message.sender_pn,
    payload.senderPn,
    payload.sender_pn,
    key.senderPn,
    key.sender_pn,
    key.participantPn,
    key.participant_pn,
    sender.phone,
    sender.whatsapp_phone,
  ]);
  const contactPhone = firstText([
    message.contactPhone,
    message.contact_phone,
    payload.contactPhone,
    payload.contact_phone,
    contact.phone,
    contact.whatsapp_phone,
    contact.waId,
    contact.wa_id,
    contact.jid,
    message.participantPn,
    message.participant_pn,
    payload.participantPn,
    payload.participant_pn,
    message.remoteJidAlt,
    message.remote_jid_alt,
    payload.remoteJidAlt,
    payload.remote_jid_alt,
    key.remoteJidAlt,
    key.remote_jid_alt,
    senderPhone,
  ]);
  return {
    ...message,
    key,
    raw,
    rawMessage,
    chatId,
    senderId,
    senderPhone,
    contactPhone,
    fromPhone: firstText([message.fromPhone, message.from_phone, payload.fromPhone, payload.from_phone, message.from, payload.from]),
    waId: firstText([message.waId, message.wa_id, payload.waId, payload.wa_id, key.remoteJid, chatId]),
    remoteJid: firstText([message.remoteJid, payload.remoteJid, key.remoteJid]),
    remoteJidAlt: firstText([message.remoteJidAlt, message.remote_jid_alt, payload.remoteJidAlt, payload.remote_jid_alt, key.remoteJidAlt, key.remote_jid_alt]),
    participant: firstText([message.participant, payload.participant, key.participant]),
    participantPn: firstText([message.participantPn, message.participant_pn, payload.participantPn, payload.participant_pn, key.participantPn, key.participant_pn]),
    senderPn: firstText([message.senderPn, message.sender_pn, payload.senderPn, payload.sender_pn, key.senderPn, key.sender_pn]),
  };
}

function isConversationAssignedToIdentity(conversation, identity) {
  const assignedUser = conversation?.assigned_user || conversation?.assignedUser || conversation?.assigned_user_id;
  const assignedId = assignedUserIdFrom(conversation);
  if (assignedId && identity.ids.includes(String(assignedId))) return true;
  const assignedEmail = String(assignedUser?.email || "").trim().toLowerCase();
  const assignedName = String(assignedUser?.nome || assignedUser?.name || "").trim().toLowerCase();
  const identityName = identity.name === "Atendente" ? "" : identity.name.toLowerCase();
  return Boolean(
    (identity.email && assignedEmail && assignedEmail === identity.email.toLowerCase()) ||
    (identityName && assignedName && assignedName === identityName)
  );
}

async function buildHeartbeatPayload(sessionId = "") {
  const identity = currentUserIdentity();
  const payload = {
    attendant: {
      id: identity.ids[0] || "",
      name: identity.name,
      email: identity.email,
    },
    conversations: [],
  };
  if (!identity.ids.length && !identity.email && !identity.name) return payload;
  try {
    const response = await run("whatsapp-conversations/list", { limit: 100 });
    const conversations = response?.data?.conversations || response?.data?.items || response?.data || [];
    const normalizedSessionId = String(sessionId || "").trim();
    payload.conversations = (Array.isArray(conversations) ? conversations : [])
      .filter(conversation => !normalizedSessionId || String(conversation?.session_id || "").trim() === normalizedSessionId)
      .filter(conversation => isConversationAssignedToIdentity(conversation, identity))
      .slice(0, 20)
      .map(compactConversation)
      .filter(conversation => conversation.id);
  } catch (error) {
    publishLog(error?.message || "Falha ao atualizar conversas do atendente conectado.", "warning");
  }
  return payload;
}

async function sendHeartbeat(config = {}) {
  send("gateway:client:heartbeat", await buildHeartbeatPayload(config.session_id));
}

function request(name, payload = {}) {
  return new Promise((resolve, reject) => {
    if (socket?.readyState !== WebSocket.OPEN) {
      reject(new Error("Gateway local desconectado."));
      return;
    }
    const id = `${name}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const timeout = window.setTimeout(() => {
      pendingRequests.delete(id);
      reject(new Error("Tempo esgotado ao aguardar o gateway local."));
    }, 8000);
    pendingRequests.set(id, { resolve, reject, timeout });
    socket.send(JSON.stringify({ id, name, payload }));
  });
}

async function ingestMessage(config, event) {
  const payload = event.payload || {};
  const normalizedMessage = normalizeIncomingMessage(payload);
  if (!payload.sessionId || payload.sessionId !== config.session_id || !normalizedMessage.chatId) return;
  if (normalizedMessage.chatType === "group" && config.receive_group_messages !== true) return;
  if (normalizedMessage.chatType === "private" && config.receive_private_messages === false) return;
  const response = await run("whatsapp-conversations/ingest-message", {
    session_id: payload.sessionId,
    message: normalizedMessage,
  });
  publishLog(`Mensagem recebida de ${normalizedMessage.senderName || normalizedMessage.senderPhone || normalizedMessage.contactPhone || normalizedMessage.chatId}.`, "success");
  send("gateway:event:ack", {
    name: event.name,
    sessionId: payload.sessionId,
    messageId: normalizedMessage.id,
  });
  if (!response.data.ignored) {
    window.dispatchEvent(new CustomEvent("stonepro:whatsapp-message", {
      detail: { ...response.data, message: response.data.message || normalizedMessage },
    }));
    if (!response.data.is_from_me) {
      window.dispatchEvent(new CustomEvent("stonepro:whatsapp-refresh"));
    }
  }
}

async function updateContactPicture(config, message) {
  const payload = message.payload || {};
  const picture = payload.picture || {};
  if (!payload.sessionId || payload.sessionId !== config.session_id || !picture.chatId || !picture.senderPictureUrl) return;
  const response = await run("whatsapp-conversations/update-contact-picture", {
    session_id: payload.sessionId,
    chat_id: picture.chatId,
    sender_picture_url: picture.senderPictureUrl,
  });
  window.dispatchEvent(new CustomEvent("stonepro:whatsapp-contact-picture", {
    detail: { conversation: response.data.conversation },
  }));
}

async function connect() {
  clearTimers();
  if (stopped || socket?.readyState === WebSocket.OPEN || socket?.readyState === WebSocket.CONNECTING) return;
  try {
    const response = await run("marketplaces/get-config-whatsapp-gateway", { tenant_id: currentTenantId() });
    const config = response.data.whatsapp_gateway_config || {};
    const token = syncGatewayTokenFromConfig(config);
    activeConfigSignature = configSignature({ ...config, gateway_token: token });
    if (!config.gateway_host || !config.session_id || !token) {
      publishStatus("not_configured");
      scheduleReconnect();
      return;
    }
    if (hasNetworkDivergence(config) && !window.confirm("O Wi-Fi/rede atual parece diferente da rede onde o Loop informou o WhatsApp. Quer tentar conectar mesmo assim?")) {
      publishStatus("network_mismatch");
      publishLog("Conexao cancelada por divergencia de rede local.", "warning");
      return;
    }
    publishStatus("connecting");
    socket = new WebSocket(gatewayUrl(config, token));
    const activeSocket = socket;
    let socketOpened = false;
    let socketErrored = false;
    socket.addEventListener("open", () => {
      if (socket !== activeSocket) return;
      socketOpened = true;
      unreachableRetries = 0;
      publishStatus("connected", { gatewayHost: config.gateway_host, gatewayPort: config.gateway_port });
      publishLog(`Conectado ao gateway ${config.gateway_host}:${config.gateway_port}.`, "success");
      void sendHeartbeat(config);
      heartbeatTimer = window.setInterval(() => void sendHeartbeat(config), HEARTBEAT_DELAY_MS);
    });
    socket.addEventListener("message", event => {
      if (socket !== activeSocket) return;
      try {
        const message = JSON.parse(event.data);
        if (message.id && pendingRequests.has(message.id)) {
          const pending = pendingRequests.get(message.id);
          pendingRequests.delete(message.id);
          window.clearTimeout(pending.timeout);
          if (message.ok) pending.resolve(message.payload);
          else pending.reject(new Error(message.message || "Falha no gateway local."));
          return;
        }
        if (message.name === "integration:auth:failed") {
          publishStatus("auth_failed");
          publishLog("Token local recusado pelo gateway.", "error");
        }
        if (message.name === "whatsapp:message:received") {
          void ingestMessage(config, message);
        }
        if (message.name === "whatsapp:message:status") {
          const statusPayload = {
            message_id: message.payload?.messageId,
            whatsapp_message_id: message.payload?.whatsappMessageId,
            delivery_status: message.payload?.deliveryStatus,
            poll_results: message.payload?.pollResults,
          };
          void run("whatsapp-conversations/update-message-status", statusPayload).then(response => {
            window.dispatchEvent(new CustomEvent("stonepro:whatsapp-message-status", {
              detail: { message: response.data?.message || statusPayload }
            }));
            window.dispatchEvent(new CustomEvent("stonepro:whatsapp-refresh"));
          });
        }
        if (message.name === "whatsapp:contact:picture") {
          void updateContactPicture(config, message);
        }
        if (message.name === "whatsapp:gateway:log") {
          publishLog(message.payload?.message || message.message || "Log do gateway WhatsApp.", message.payload?.level || "info");
        }
      } catch (error) {
        console.error("Falha ao processar evento do gateway WhatsApp.", error);
      }
    });
    socket.addEventListener("close", () => {
      if (socket !== activeSocket) return;
      socket = null;
      clearTimers();
      if (status === "auth_failed") return;
      if (!socketOpened && socketErrored) {
        publishStatus("unreachable", { gatewayHost: config.gateway_host, gatewayPort: config.gateway_port });
        if (unreachableRetries === 0) {
          publishLog(`Nao foi possivel acessar o gateway local em ${config.gateway_host}:${config.gateway_port}.`, "error");
        }
        scheduleGatewayUnavailableReconnect();
        return;
      }
      publishStatus("disconnected");
      publishLog("Gateway local desconectado.", "warning");
      scheduleReconnect();
    });
    socket.addEventListener("error", () => {
      socketErrored = true;
      if (socket === activeSocket) socket?.close();
    });
  } catch (error) {
    publishStatus("error", { message: error.message });
    publishLog(error.message || "Falha ao conectar no gateway.", "error");
    scheduleReconnect();
  }
}

export function startWhatsappGatewayRuntime() {
  if (!stopped && (socket?.readyState === WebSocket.OPEN || socket?.readyState === WebSocket.CONNECTING || retryTimer)) return;
  stopped = false;
  void connect();
}

export function stopWhatsappGatewayRuntime({ silent = false } = {}) {
  stopped = true;
  clearTimers();
  socket?.close();
  socket = null;
  if (!silent) publishStatus("disconnected");
}

export function restartWhatsappGatewayRuntime() {
  unreachableRetries = 0;
  stopWhatsappGatewayRuntime({ silent: true });
  startWhatsappGatewayRuntime();
}

export function refreshWhatsappGatewayRuntimeConfig(config = {}) {
  const token = syncGatewayTokenFromConfig(config);
  const nextSignature = configSignature({ ...config, gateway_token: token });
  if (!nextSignature || nextSignature === activeConfigSignature) return;
  publishLog("Configuração do gateway atualizada no StonePro. Reconectando...", "info");
  restartWhatsappGatewayRuntime();
}

export function getWhatsappGatewayStatus() {
  return status;
}

export function getWhatsappGatewayLogs() {
  return logs;
}

export function sendWhatsappMessage(payload) {
  return request("whatsapp:message:send", payload).then(response => {
    publishLog(`Mensagem enviada para ${payload.chatId}.`, "success");
    return response;
  });
}
